<template>
  <section id="anrchor" class="trial-section" aria-labelledby="trial-title">
    <h2 id="trial-title">{{ $portalText('trialForm', 't001') }}</h2>
    <div class="trial-layout">
      <div class="trial-card">
        <p class="trial-intro">{{ $portalText('trialForm', 't002') }}</p>
        <Alert v-if="error" :title="error" :type="errorType" show-icon :closable="false" class="trial-alert" />
        <Form ref="form" :model="form" :rules="rules" label-position="top" @submit.native.prevent="submit">
          <div class="trial-grid">
            <FormItem :label="$portalText('trialForm', 't005')" prop="companyName">
              <Input v-model.trim="form.companyName" :maxlength="160" autocomplete="organization" :placeholder="$portalText('trialForm', 't004')" />
            </FormItem>
            <FormItem :label="$portalText('trialForm', 't007')" prop="contactName">
              <Input v-model.trim="form.contactName" :maxlength="80" autocomplete="name" :placeholder="$portalText('trialForm', 't006')" />
            </FormItem>
          </div>
          <FormItem :label="$portalText('trialForm', 't010')" prop="email">
            <Input v-model.trim="form.email" :maxlength="254" autocomplete="email" :placeholder="$portalText('trialForm', 't008')" />
            <span class="trial-help">{{ $portalText('trialForm', 't009') }}</span>
          </FormItem>
          <FormItem :label="$portalText('trialForm', 't013')" prop="phone">
            <div class="trial-phone">
              <Input v-model.trim="form.phoneCountryCode" aria-label="国际区号" :placeholder="$portalText('trialForm', 't011')" :maxlength="5" />
              <Input v-model.trim="form.phone" autocomplete="tel-national" :placeholder="$portalText('trialForm', 't012')" :maxlength="15" />
            </div>
          </FormItem>
          <FormItem :label="$portalText('trialForm', 't018')" prop="department">
            <RadioGroup v-model="form.department">
              <Radio label="QUALITY">{{ $portalText('trialForm', 't014') }}</Radio><Radio label="IT">{{ $portalText('trialForm', 't015') }}</Radio>
              <Radio label="PROCUREMENT">{{ $portalText('trialForm', 't016') }}</Radio><Radio label="OTHER">{{ $portalText('trialForm', 't017') }}</Radio>
            </RadioGroup>
          </FormItem>
          <FormItem v-if="form.department === 'OTHER'" :label="$portalText('trialForm', 't020')" prop="otherDepartment">
            <Input v-model.trim="form.otherDepartment" :maxlength="100" :placeholder="$portalText('trialForm', 't019')" />
          </FormItem>
          <FormItem :label="$portalText('trialForm', 't022')">
            <Input v-model.trim="form.requirement" type="textarea" :rows="3" :maxlength="2000" show-word-limit :placeholder="$portalText('trialForm', 't021')" />
          </FormItem>
          <FormItem prop="privacyAccepted" class="trial-consent">
            <Checkbox v-model="form.privacyAccepted">{{ $portalText('trialForm', 't023') }}</Checkbox>
            <button class="trial-link" type="button" @click="privacyVisible = true">{{ $portalText('trialForm', 't024') }}</button>
          </FormItem>
          <Button type="primary" native-type="submit" :loading="submitting" :disabled="!config.open || submitting || $portalPreview" class="trial-submit">{{ $portalText('trialForm', 't025') }}</Button>
          <button v-if="!config.open" type="button" class="trial-link" @click="loadConfig">{{ $portalText('trialForm', 't026') }}</button>
          <p class="trial-help">首次激活成功后开始计算试用期，默认 {{ trialDays }} 天。</p>
          <p class="trial-account-help"><button type="button" class="trial-link" @click="accountHelpVisible = true">{{ $portalText('trialForm', 'accountSupportLabel') }}</button></p>
        </Form>
      </div>
      <img class="trial-image" :src="cardBackgroundImg" :alt="$portalText('trialForm', 't027')" />
    </div>
    <Dialog :title="$portalText('trialForm', 't042')" :visible.sync="privacyVisible" width="min(920px, 100%)" top="0" class="trial-privacy-modal" custom-class="trial-privacy-dialog" append-to-body>
      <div class="trial-privacy-scroll"><p class="trial-privacy">{{ config.privacyText || '隐私说明正在加载，请稍后再试。' }}</p></div>
      <span slot="footer"><Button type="primary" class="trial-privacy-button" @click="privacyVisible = false">{{ $portalText('trialForm', 't041') }}</Button></span>
    </Dialog>
    <Dialog :title="$portalText('trialForm', 'accountHelpTitle')" :visible.sync="accountHelpVisible" width="min(660px, 94vw)" append-to-body>
      <div class="trial-feedback-body"><p>{{ $portalText('trialForm', 'accountHelpBody') }}</p><p>{{ $portalText('trialForm', 'accountHelpFollowup') }}</p><p v-if="config.supportEmail">客服邮箱：<a :href="'mailto:' + config.supportEmail">{{ config.supportEmail }}</a></p><p v-if="config.supportPhone">客服电话：<a :href="'tel:' + config.supportPhone">{{ config.supportPhone }}</a></p></div>
      <span slot="footer"><Button type="primary" @click="accountHelpVisible = false">{{ $portalText('trialForm', 'accountHelpClose') }}</Button></span>
    </Dialog>
  </section>
</template>

<script>
import { portalData } from '@/portal/runtime';

import { Form, FormItem, Input, RadioGroup, Radio, Button, Checkbox, Dialog, Alert } from 'element-ui';
import { trialRequest, newRequestKey } from '@/utils/trialApi';
import { resolveTrialDays } from '@/utils/trialPolicy.mjs';
import cardBackgroundImg from '@/assets/images/formNav/card_background.png';
export default {
  portalScope: 'trialForm',
  components: { Form, FormItem, Input, RadioGroup, Radio, Button, Checkbox, Dialog, Alert },
  data() {
    const required = message => ({ required: true, message, trigger: 'blur' });
    return portalData('trialForm', {
      accountHelpVisible: false,
      cardBackgroundImg, config: {}, error: '', errorType: 'error', submitting: false, privacyVisible: false,
      requestKey: '', submittedFingerprint: '',
      form: { companyName: '', contactName: '', email: '', phoneCountryCode: '+86', phone: '',
        department: '', otherDepartment: '', requirement: '', privacyAccepted: false },
      rules: {
        companyName: [required('请输入完整公司名称'), { min: 2, max: 160, message: '公司名称须为2–160个字符', trigger: 'blur' }],
        contactName: [required('请输入联系人'), { min: 2, max: 80, message: '联系人须为2–80个字符', trigger: 'blur' }],
        email: [required('请输入企业邮箱'), { validator: (rule, value, done) =>
          done(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? undefined : new Error('请输入有效企业邮箱')), trigger: 'blur' }],
        phone: [{ validator: (rule, value, done) => done(
          /^\+[1-9][0-9]{0,3}$/.test(this.form.phoneCountryCode) && /^[0-9]{5,15}$/.test(value)
            ? undefined : new Error('请填写正确的国际区号和5–15位电话号码')), trigger: 'blur' }],
        department: [required('请选择部门或职位')],
        otherDepartment: [required('请补充部门或职位')],
        privacyAccepted: [{ validator: (rule, value, done) => done(value ? undefined : new Error('请阅读并同意隐私说明')), trigger: 'change' }],
      },
    });
  },
  computed: { trialDays() { return resolveTrialDays(this.config.defaultTrialDays); } },
  mounted() { this.loadConfig(); },
  methods: {
    async loadConfig() {
      this.errorType = 'error';
      try { this.config = await trialRequest('/config'); this.error = this.config.open ? '' : '申请入口暂未开放，请稍后再试。'; }
      catch (error) { this.error = error.message; }
    },
    submit() {
      if (this.submitting || !this.config.open || this.$portalPreview) return;
      this.$refs.form.validate(async valid => {
        if (!valid || this.submitting) return;
        this.submitting = true; this.error = ''; this.errorType = 'error';
        const data = { ...this.form, privacyVersion: this.config.privacyVersion, sourcePath: this.$route.path };
        const fingerprint = JSON.stringify(data);
        if (!this.requestKey || fingerprint !== this.submittedFingerprint) {
          this.requestKey = newRequestKey(); this.submittedFingerprint = fingerprint;
        }
        try {
          const result = await trialRequest('/apply', { ...data, requestKey: this.requestKey });
          if (!result.accepted) {
            this.error = '申请暂未成功提交，请稍后重试，已填写的信息会为您保留。';
            return;
          }
          sessionStorage.setItem('dcmp-trial-result', JSON.stringify(result));
          // A confirmed success ends this intent; an explicit new submission gets a fresh key.
          this.requestKey = ''; this.submittedFingerprint = '';
          this.$router.push('/trial/result');
        } catch (error) { this.error = error.message; }
        finally { this.submitting = false; }
      });
    },
  },
};
</script>

<style scoped>
.trial-section {
  padding: 48px 24px 64px;
  background: linear-gradient(110deg, #eaf4ff, #f7faff);
  font-family: inherit;
  font-size: 20px;
  line-height: 1.7;
  color: #1c1c1c;
}
/* Keep form text on the portal font instead of the global body * size reset. */
.trial-card,
.trial-section ::v-deep .trial-card * {
  font-family: inherit;
  font-size: inherit;
}
.trial-section h2 {
  margin: 0 0 32px;
  color: #181818;
  font-size: clamp(32px, 0.4rem, 44px);
  font-weight: 600;
  line-height: 1.4;
  text-align: center;
}
.trial-layout { max-width: 1200px; margin: auto; display: grid; grid-template-columns: minmax(0, 760px) minmax(240px, 1fr); gap: 40px; align-items: stretch; }
.trial-card { min-width: 0; background: #fff; padding: 32px 36px; border-radius: 8px; box-shadow: 0 8px 32px #17466f0a; }
.trial-section .trial-intro { margin: 0 0 28px; color: #333; font-size: 22px; line-height: 1.8; }
.trial-image { width: 100%; height: 100%; max-height: 850px; object-fit: cover; border-radius: 8px; }
.trial-grid { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 20px; }
.trial-phone { display: grid; grid-template-columns: 100px minmax(0, 1fr); gap: 12px; }
.trial-alert { margin-bottom: 16px; padding: 16px 20px; }
.trial-section ::v-deep .trial-alert .el-alert__title { font-size: 20px; font-weight: 500; line-height: 1.7; }
.trial-section ::v-deep .trial-alert.el-alert--warning { color: #795200; background: #fff8e8; }
.trial-section ::v-deep .trial-alert.el-alert--error { color: #b42318; background: #fff1f0; }
.trial-section .trial-review-trigger { display: block; margin: 0 0 24px; padding-left: 0; }
.trial-feedback-title { margin: 0; color: #1c1c1c; font-size: 26px; font-weight: 600; line-height: 1.5; }
.trial-feedback-body { color: #333; font-size: 18px; line-height: 1.8; }
.trial-feedback-body * { font-size: inherit; line-height: inherit; }
.trial-feedback-body p, .trial-feedback-body li, .trial-feedback-body strong, .trial-feedback-body a { font-size: inherit; line-height: inherit; }
.trial-feedback-body p { margin: 0 0 16px; }
.trial-feedback-body .trial-feedback-intro { color: #1c1c1c; font-size: 20px; font-weight: 500; }
.trial-feedback-body ol { margin: 0; padding-left: 26px; list-style: decimal; }
.trial-feedback-body li { margin: 0 0 14px; }
.trial-feedback-body a { color: #005c85; text-decoration: underline; overflow-wrap: anywhere; }
.trial-feedback-contact { margin-top: 20px; padding: 16px; background: #f1f6fb; border-radius: 6px; }
.trial-feedback-contact p:last-child { margin-bottom: 0; }
.trial-feedback-button { min-height: 48px; padding: 10px 24px; background: #005c85; border-color: #005c85; font-size: 18px; }
.trial-feedback-button ::v-deep span { font-size: inherit; }
.trial-section .trial-help { display: block; margin-top: 10px; color: #666; font-size: 16px; line-height: 1.7; }
.trial-section .trial-link { border: 0; background: none; color: #005c85; cursor: pointer; padding: 0 6px; font-size: 18px; line-height: 1.7; text-decoration: underline; }
.trial-section .trial-submit { width: 100%; height: 54px; background: #005c85; border-color: #005c85; font-size: 20px; font-weight: 600; }
.trial-section .trial-submit:hover,
.trial-section .trial-submit:focus { background: #004c70; border-color: #004c70; }
.trial-privacy { color: #333; font-size: 18px; white-space: pre-wrap; line-height: 1.8; }
.trial-section ::v-deep .el-form-item__label { padding-bottom: 8px; color: #333; font-size: 20px; font-weight: 500; line-height: 1.6; }
.trial-section ::v-deep .el-form-item { margin-bottom: 26px; }
.trial-section ::v-deep .el-form-item__content { line-height: 1.7; }
.trial-section ::v-deep .el-input__inner { height: 52px; color: #1c1c1c; font-size: 20px; line-height: 52px; }
.trial-section ::v-deep .el-textarea__inner { min-height: 128px; padding: 12px 15px 30px; color: #1c1c1c; font-size: 20px; line-height: 1.7; }
.trial-section ::v-deep .el-input__inner::placeholder,
.trial-section ::v-deep .el-textarea__inner::placeholder { color: #767676; opacity: 1; }
.trial-section ::v-deep .el-input.is-active .el-input__inner,
.trial-section ::v-deep .el-input__inner:focus,
.trial-section ::v-deep .el-textarea__inner:focus { border-color: #005c85; }
.trial-section ::v-deep .el-radio { margin: 0 24px 8px 0; color: #333; font-size: 18px; line-height: 1.7; }
.trial-section ::v-deep .el-radio__label,
.trial-section ::v-deep .el-checkbox__label { color: #333; font-size: 18px; }
.trial-section ::v-deep .el-radio__inner,
.trial-section ::v-deep .el-checkbox__inner { width: 18px; height: 18px; }
.trial-section ::v-deep .el-checkbox__inner::after { left: 6px; top: 2px; }
.trial-section ::v-deep .el-radio__input.is-checked .el-radio__inner,
.trial-section ::v-deep .el-checkbox__input.is-checked .el-checkbox__inner { background: #005c85; border-color: #005c85; }
.trial-section ::v-deep .el-radio__input.is-checked + .el-radio__label { color: #005c85; }
.trial-section ::v-deep .el-input .el-input__count,
.trial-section ::v-deep .el-textarea .el-input__count { color: #666; font-size: 16px; line-height: 1.5; }
.trial-section ::v-deep .el-form-item__error { font-size: 16px; line-height: 1.5; }
@media (max-width: 800px) {
  .trial-feedback-title { font-size: 22px; }
  .trial-feedback-body, .trial-feedback-body .trial-feedback-intro { font-size: 18px; }
  .trial-section ::v-deep .trial-alert .el-alert__title { font-size: 18px; }
  .trial-section { padding: 32px 16px; font-size: 18px; }
  .trial-layout { grid-template-columns: minmax(0, 1fr); }
  .trial-image { display: none; }
  .trial-card { padding: 24px 20px; }
  .trial-grid { grid-template-columns: minmax(0, 1fr); gap: 0; }
  .trial-phone { grid-template-columns: 80px minmax(0, 1fr); }
  .trial-section h2 { font-size: 32px; }
  .trial-section .trial-intro { font-size: 20px; }
  .trial-section ::v-deep .el-form-item__label,
  .trial-section ::v-deep .el-input__inner,
  .trial-section ::v-deep .el-textarea__inner { font-size: 18px; }
  .trial-section ::v-deep .el-radio { margin-right: 18px; }
  .trial-section ::v-deep .el-checkbox__label,
  .trial-section .trial-link { font-size: 16px; }
  .trial-section .trial-submit { font-size: 18px; }
}
</style>

<style>
.trial-privacy-modal { display: flex; align-items: center; justify-content: center; padding: 24px; box-sizing: border-box; overflow: hidden; }
.trial-privacy-dialog { display: flex; flex-direction: column; max-height: min(82vh, 820px); max-height: min(82dvh, 820px); margin: 0; overflow: hidden; border-radius: 12px; box-sizing: border-box; }
.trial-privacy-dialog .el-dialog__header { flex: 0 0 auto; padding: 22px 28px 18px; border-bottom: 1px solid #e4eaf1; }
.trial-privacy-dialog .el-dialog__title { font-size: 24px; line-height: 1.4; color: #1b3348; font-weight: 600; }
.trial-privacy-dialog .el-dialog__headerbtn { top: 18px; right: 18px; display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; font-size: 20px; }
.trial-privacy-dialog .el-dialog__close { font-size: 20px; }
.trial-privacy-dialog .el-dialog__body { display: flex; flex: 1 1 auto; min-height: 0; overflow: hidden; padding: 20px 28px; }
.trial-privacy-dialog .trial-privacy-scroll { min-height: 0; width: 100%; overflow-y: auto; overscroll-behavior: contain; padding-right: 12px; scrollbar-gutter: stable; }
.trial-privacy-dialog .trial-privacy { white-space: pre-line; color: #33465a; font-size: 18px; line-height: 1.9; margin: 0; }
.trial-privacy-dialog .el-dialog__footer { flex: 0 0 auto; padding: 16px 28px 20px; border-top: 1px solid #e4eaf1; text-align: right; }
.trial-privacy-dialog .trial-privacy-button { min-width: 144px; min-height: 44px; padding: 10px 24px; border-radius: 6px; background: #005c85; border-color: #005c85; color: #fff; font-size: 18px; line-height: 1.4; }
.trial-privacy-dialog .trial-privacy-button * { font-size: inherit; }
.trial-privacy-dialog .trial-privacy-button:hover, .trial-privacy-dialog .trial-privacy-button:focus { background: #004c70; border-color: #004c70; }
.trial-privacy-dialog .trial-privacy-button:focus-visible { outline: 3px solid #67afd1; outline-offset: 3px; }
.trial-section .trial-account-help { margin: 12px 0 0; font-size: 18px; line-height: 1.8; color: #52677d; }
@media(max-width: 600px), (max-height: 600px) {
  .trial-privacy-modal { padding: 16px; }
  .trial-privacy-dialog { max-height: calc(100vh - 32px); max-height: calc(100dvh - 32px); }
  .trial-privacy-dialog .el-dialog__header { padding: 18px 20px 14px; }
  .trial-privacy-dialog .el-dialog__title { font-size: 22px; }
  .trial-privacy-dialog .el-dialog__body { padding: 16px 20px; }
  .trial-privacy-dialog .trial-privacy { font-size: 16px; }
  .trial-privacy-dialog .el-dialog__footer { padding: 12px 20px 16px; }
  .trial-privacy-dialog .trial-privacy-button { min-width: 128px; font-size: 16px; }
}
@media(max-width: 600px) { .trial-section .trial-account-help { font-size: 16px; } }
</style>
